import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { captureEmail, isValidEmail } from '../services/emailCapture';
import StyledSocialMenu from '../components/StyledSocialMenu';

const GlobalStyle = createGlobalStyle`
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 105, 180, 0.3) #1a1a1a;
  }

  *::-webkit-scrollbar {
    width: 12px;
    height: 12px;
    background-color: #1a1a1a;
  }

  *::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 8px;
    margin: 5px;
  }

  *::-webkit-scrollbar-thumb {
    background: rgba(255, 105, 180, 0.3);
    border: 3px solid #1a1a1a;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 105, 180, 0.5);
    }
  }

  body {
    margin: 0;
    padding: 0;
    background: #1a1a1a;
    color: #ffffff;
    font-family: 'Share Tech Mono', monospace;
    overflow-x: hidden;
  }
`;

const flicker = keyframes`
  0%, 100% { opacity: 1; }
  95% { opacity: 0.98; }
`;

const vhsScanline = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100vh);
  }
`;

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4rem 2rem;
  background: #1a1a1a;
  
  /* VHS TV effect background */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(255, 255, 255, 0.03) 2px,
        rgba(255, 255, 255, 0.03) 4px
      );
    pointer-events: none;
    z-index: 1;
    opacity: 0.5;
  }
  
  /* VHS noise texture */
  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 2;
    animation: ${flicker} 8s infinite;
  }
`;

const PinkScanlines = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 3;
  overflow: hidden;
  
  span {
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 105, 180, 0.4) 20%,
      rgba(255, 105, 180, 0.6) 50%,
      rgba(255, 105, 180, 0.4) 80%,
      transparent
    );
    box-shadow: 
      0 0 10px rgba(255, 105, 180, 0.5),
      0 0 20px rgba(255, 105, 180, 0.3);
    animation: ${vhsScanline} linear infinite;
  }
  
  span:nth-of-type(1) {
    animation-duration: 15s;
    animation-delay: 0s;
  }
  
  span:nth-of-type(2) {
    animation-duration: 20s;
    animation-delay: 5s;
  }
  
  span:nth-of-type(3) {
    animation-duration: 18s;
    animation-delay: 10s;
  }
`;

const PageContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 800px;
  margin: 0 auto;
`;

const PageSection = styled.div<{ $isVisible: boolean }>`
  display: ${props => props.$isVisible ? 'block' : 'none'};
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.5s ease;
`;

const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  text-align: center;
  transition: all 0.5s ease;
`;

const Title = styled.h1`
  color: #ff69b4;
  margin-bottom: 2rem;
  font-family: 'Share Tech Mono', monospace;
  font-size: 3.5rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 4px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionTitle = styled.h2`
  color: #ff69b4;
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 3px;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 600px;
  text-align: center;
`;

const EnterButton = styled.button`
  background: rgba(255, 105, 180, 0.1);
  border: 2px solid rgba(255, 105, 180, 0.3);
  color: #ff69b4;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.2rem;
  font-family: 'Share Tech Mono', monospace;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 2px;
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(255, 105, 180, 0.2);
    border-color: #ff69b4;
    transform: translateY(-3px);
    box-shadow: 0 0 20px rgba(255, 105, 180, 0.3);
  }
`;

const EmailForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
`;

const EmailInput = styled.input`
  background: rgba(35, 35, 50, 0.8);
  border: 2px solid rgba(255, 105, 180, 0.3);
  color: #ffffff;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Share Tech Mono', monospace;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff69b4;
    box-shadow: 0 0 15px rgba(255, 105, 180, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  background: rgba(255, 105, 180, 0.1);
  border: 2px solid rgba(255, 105, 180, 0.3);
  color: #ff69b4;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-family: 'Share Tech Mono', monospace;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 2px;

  &:hover:not(:disabled) {
    background: rgba(255, 105, 180, 0.2);
    border-color: #ff69b4;
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(255, 105, 180, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ $isError?: boolean }>`
  color: ${props => props.$isError ? '#ff4444' : '#44ff44'};
  text-align: center;
  padding: 1rem;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  border: 2px solid ${props => props.$isError ? '#ff4444' : '#44ff44'};
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const NavButton = styled.button`
  background: rgba(255, 105, 180, 0.1);
  border: 2px solid rgba(255, 105, 180, 0.3);
  color: #ff69b4;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Share Tech Mono', monospace;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 105, 180, 0.2);
    border-color: #ff69b4;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      transform: none;
    }
  }
`;

const BackButton = styled.button`
  position: fixed;
  top: 2rem;
  left: 2rem;
  background: rgba(255, 105, 180, 0.1);
  border: 1px solid rgba(255, 105, 180, 0.3);
  border-radius: 8px;
  padding: 0.8rem 1.5rem;
  color: rgba(255, 105, 180, 0.9);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 105, 180, 0.2);
    border-color: rgba(255, 105, 180, 0.6);
    color: #ff69b4;
    transform: translateX(-5px);
  }

  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }
`;

const TransmissionPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ message: string; isError: boolean } | null>(null);

  const handleEnterBroadcast = () => {
    setCurrentPage(1);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidEmail(email)) {
      setSubmitStatus({ message: 'Invalid transmission coordinates', isError: true });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await captureEmail(email);
      setSubmitStatus({ message: 'Transmission received! Welcome to the frequency.', isError: false });
      setEmail('');
      setTimeout(() => setCurrentPage(2), 2000);
    } catch (error) {
      setSubmitStatus({ message: 'Signal disrupted. Please try again.', isError: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <GlobalStyle />
      <Container>
        <PinkScanlines>
          <span></span>
          <span></span>
          <span></span>
        </PinkScanlines>
        <PageContent>
          <BackButton onClick={() => navigate('/music')}>← BACK TO MUSIC</BackButton>
          <StyledSocialMenu theme="pink" position="top-right" />
          
          {/* Hero Page */}
          <HeroSection style={{ display: currentPage === 0 ? 'flex' : 'none' }}>
            <Title>A5OMIC: TRANSMISSION</Title>
            <Subtitle>
              SIGNAL DETECTED. FREQUENCY LOCKED. 
              Breaking through the static to reach true believers. 
              Transform from passive listener to active participant in the A5omic universe.
            </Subtitle>
            <EnterButton onClick={handleEnterBroadcast}>
              Enter Broadcast
            </EnterButton>
          </HeroSection>

          {/* Email Capture Page */}
          <PageSection $isVisible={currentPage === 1}>
            <SectionTitle>Join the Frequency</SectionTitle>
            <Subtitle>
              Receive exclusive transmissions, unreleased tracks, and behind-the-scenes access 
              to the A5omic universe. The signal is strongest for those who subscribe.
            </Subtitle>
            <EmailForm onSubmit={handleEmailSubmit}>
              <EmailInput 
                type="email" 
                placeholder="Enter your transmission coordinates (email)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Transmitting...' : 'Lock Signal'}
              </SubmitButton>
              {submitStatus && (
                <StatusMessage $isError={submitStatus.isError}>
                  {submitStatus.message}
                </StatusMessage>
              )}
            </EmailForm>
            <NavigationButtons>
              <NavButton onClick={() => setCurrentPage(0)}>← Previous</NavButton>
              <NavButton onClick={() => setCurrentPage(2)}>Skip →</NavButton>
            </NavigationButtons>
          </PageSection>

          {/* Thank You Page */}
          <PageSection $isVisible={currentPage === 2}>
            <SectionTitle>Signal Locked</SectionTitle>
            <Subtitle>
              You're now tuned into the A5omic frequency. Stay connected for exclusive transmissions, 
              unreleased tracks, and behind-the-scenes access. The broadcast continues...
            </Subtitle>
            <EnterButton onClick={() => navigate('/music')}>
              Return to Music
            </EnterButton>
          </PageSection>
        </PageContent>
      </Container>
    </>
  );
};

export default TransmissionPage;
